import { createApiClient } from '@/api/client';

export interface CreateUserPayload {
    token: string;
    email: string;
    password: string;
}

export interface InviteUserPayload {
    firstName: string;
    lastName: string;
    email: string;
    funeralHomeId?: number | string | null;
    supplierId?: number | string;
    ownerId?: number;
    userType?: number;
    phoneNo?: string;
}

export interface InviteUserResponse {
    id: number;
    landLine?: boolean;
}

export interface UpdateUserPayload {
    id: number;
    firstName: string;
    lastName: string;
    funeralHomeId?: number | string | null;
    supplierId?: number | string;
    ownerId?: number;
    userType?: number;
    phoneNo?: string;
}

export interface UpdateUserResponse {
    id: number;
    landLine?: boolean;
}

export interface UpdateUserPhoneTypeResponse {
    phoneType: number;
}

export interface UpdateUserPhoneNumberResponse {
    landLine?: boolean;
}

export interface CreateAdminUserManualPayload {
    password: string;
}

export interface InviteLinkResponse {
    authLink: string;
}

export interface UserDTO {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    phoneNo?: string | null;
    landLine?: boolean | null;
    landLineOverride?: boolean | null;
}

export async function createUser(payload: CreateUserPayload): Promise<void> {
    const api = createApiClient();
    // The invited user has no Auth0 session yet, so don't try to fetch an access token for this call
    await api.post('/users/create', payload, { skipAuth: true });
}

export async function getUser(userId: number | string): Promise<UserDTO> {
    const api = createApiClient();
    const response = await api.get(`/users/${userId}`);

    return response.data as UserDTO;
}

export async function inviteUser(payload: InviteUserPayload): Promise<InviteUserResponse> {
    const api = createApiClient();
    const response = await api.post('/users/invite', payload);
    return response.data as InviteUserResponse;
}

export async function createAdminUserManual(
    userId: number,
    payload: CreateAdminUserManualPayload,
): Promise<void> {
    const api = createApiClient();
    await api.post(`/users/create-admin-manual/${userId}`, payload);
}

export async function updateUser(payload: UpdateUserPayload): Promise<UpdateUserResponse> {
    const api = createApiClient();
    const response = await api.put('/users/', payload);
    return response.data as UpdateUserResponse;
}

export async function updateUserPhoneType(
    userId: number | string,
    phoneType: number | string,
): Promise<UpdateUserPhoneTypeResponse> {
    const api = createApiClient();
    const response = await api.post(`/users/update-phone-type/${userId}/${phoneType}`);

    return response.data as UpdateUserPhoneTypeResponse;
}

export async function updateUserPhoneNumber(
    userId: number | string,
    phoneNumber: string,
): Promise<UpdateUserPhoneNumberResponse> {
    const api = createApiClient();
    const response = await api.post(`/user/phoneNumber?id=${userId}&phoneNumber=${encodeURIComponent(phoneNumber)}`);

    return response.data as UpdateUserPhoneNumberResponse;
}

export async function getUserInviteLink(userId: number): Promise<InviteLinkResponse> {
    const api = createApiClient();
    const response = await api.get(`/users/invite-link/?id=${userId}`);
    return response.data as InviteLinkResponse;
}

export async function resendUserInvite(userId: number): Promise<void> {
    const api = createApiClient();
    await api.post(`/users/resend?id=${userId}`);
}
